import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '@platform/ui-kit';
import {
  api,
  type SpnDiscoveryCompany,
  type SpnInstructionKnob,
  type SpnInstructionModule,
} from '../../../api';
import { HelpPanel, type Help } from './HelpPanel';
import './NewDiscovery.css';

/**
 * Strategic Portfolio Navigator → Discoveries → New Discovery.
 *
 * REDESIGNED, not ported (owner's ruling, 2026-08-04, against the prototype he approved).
 * Three things changed from the screen that came across from `spn_template`:
 *
 *   1. COMPANY IS NOT A CHOICE. It is read from Administration → Company Profile — name
 *      and SIC codes — and shown read-only. This install serves one client; a discovery
 *      that could be pointed at a different company would be reporting on the wrong
 *      business, and the SIC codes are analysis input, not decoration.
 *
 *   2. VALIDITY AND ANALYSIS DEPTH ARE GONE. Validity, because a finding is valid until
 *      it is acted on or disproved, not for a fixed twelve months. Depth, because a cap
 *      of "8 per area" limits the wrong thing — the useful control is what the model is
 *      told to look for, not how many rows it is allowed to return.
 *
 *   3. INSTRUCTIONS ARE A LIBRARY. Modelled on APR's licensing instructions, which the
 *      owner asked for by name: each module is editable, keeps its shipped default so
 *      edits can be reverted, and carries knobs whose values inject directives into the
 *      compiled prompt. Tick the modules a run should use.
 *
 * Business areas gain a tri-state Select all and free-text additions, because the source's
 * eight do not cover every client — Regulatory Affairs and Field Service were the owner's
 * own examples.
 *
 * Start analysis sends the compiled prompt and lands on the session, which polls while the
 * model works. The run is NOT held open in this request — it takes minutes.
 */

const NEW_HELP: Help = {
  title: 'New Discovery',
  icon: '🔍',
  purpose:
    'Set up an analysis of where this business is losing margin. You choose the business areas to examine and the instruction modules that say what to look for; the company itself comes from Administration &rarr; Company Profile and is not a choice here.',
  doHere: [
    'Tick the <b>business areas</b> to analyse, and add your own if the eight do not cover it.',
    'Name your <b>business units</b> — divisions, sites, entities — and every finding is attributed to one.',
    'Open a business area in the module library and tick the <b>instruction modules</b> this run should use. Nothing is selected to begin with.',
    'Set the <b>run settings</b>. <b>Depth</b> is the number of opportunities per business area, from 5 to 30.',
    'Use <b>Preview compiled prompt</b> to read exactly what will be sent before spending anything.',
  ],
  fits: 'Strategic Portfolio Navigator &rarr; Discoveries &rarr; New',
  firstTime:
    'One business area, the base module plus two or three from that area, Depth set to scan. That is a single model call and about a minute.',
  sections: [
    {
      label: 'What a run costs',
      items: [
        { term: 'One call', color: '#1565c0', def: 'Per business area, three running at once. Eight areas is eight calls and several minutes.' },
        { term: 'Depth', color: '#8a5a12', def: 'Sets opportunities per area, not how hard it tries — <b>scan</b> 5, <b>standard</b> 10, <b>deep</b> 20, <b>exhaustive</b> 30.' },
      ],
      note: 'Eight areas on exhaustive is up to 240 opportunities. The run keeps going if you close the tab.',
    },
  ],
};

/** The value a knob is running at: unsaved screen edit, then stored, then shipped default. */
function knobValue(
  knob: SpnInstructionKnob,
  stored: Record<string, string> | null,
  draft: Record<string, string> | undefined,
): string {
  return draft?.[knob.key] ?? stored?.[knob.key] ?? knob.default ?? knob.values[0] ?? '';
}

export function NewDiscovery() {
  const navigate = useNavigate();

  const [company, setCompany] = useState<SpnDiscoveryCompany | null>(null);
  const [areas, setAreas] = useState<string[]>([]);
  const [modules, setModules] = useState<SpnInstructionModule[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  // ── the run's own selections ───────────────────────────────────────────────
  const [checkedAreas, setCheckedAreas] = useState<Set<string>>(new Set());
  const [customAreas, setCustomAreas] = useState<string[]>([]);
  const [areaDraft, setAreaDraft] = useState('');
  const [runModules, setRunModules] = useState<Set<string>>(new Set());
  /** The client's own divisions, sites or entities. Empty = the business as a whole. */
  const [businessUnits, setBusinessUnits] = useState<string[]>([]);
  const [unitDraft, setUnitDraft] = useState('');

  // ── the open module in the editor ──────────────────────────────────────────
  const [openId, setOpenId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [descDraft, setDescDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  /** Which area groups are expanded. Empty = all collapsed, which is the default. */
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  /** Run-level knob values — depth, priority, evidence bar. See `settingsModule` below. */
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [configDraft, setConfigDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [compiled, setCompiled] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [tokenBudget, setTokenBudget] = useState(100_000);

  const allBox = useRef<HTMLInputElement>(null);

  const say = (message: string) => {
    setFlash(message);
    window.setTimeout(() => setFlash(null), 2200);
  };

  /** Load a module into the editor, discarding whatever was being edited. */
  const open = useCallback((module: SpnInstructionModule) => {
    setOpenId(module.id);
    setNameDraft(module.name ?? '');
    setDescDraft(module.description ?? '');
    setBodyDraft(module.body ?? '');
    setConfigDraft({ ...(module.config ?? {}) });
  }, []);

  const toggleGroup = (area: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (!next.delete(area)) next.add(area);
      return next;
    });

  useEffect(() => {
    let active = true;
    Promise.all([api.spn.getNewDiscovery(), api.session()])
      .then(([form, session]) => {
        if (!active) return;
        setCompany(form.company);
        setAreas(form.businessAreas);
        setModules(form.modules);
        setTokenBudget(form.tokenBudget);
        setIsAdmin(Boolean(session.user?.isAdmin));
        // The run settings start at whatever the knob-carrying module has stored.
        const withKnobs = form.modules.find((m) => (m.config_schema?.length ?? 0) > 0);
        if (withKnobs) setSettings({ ...(withKnobs.config ?? {}) });
        // NOTHING is ticked, and no group is expanded (owner's ruling, 2026-08-04). With 40+
        // modules a helpful default is not helpful — it is a large, expensive run the user
        // did not choose. Selection is deliberate, area by area.
      })
      .catch((e) => {
        if (active) setError(e?.message || 'Could not load the form.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  /* Select all is TRI-STATE: ticked when every area is on, indeterminate when some are. A
     plain checkbox that only reads "all or nothing" lies about the middle case, which is
     the case you are usually in. `indeterminate` is a DOM property with no HTML attribute,
     so it has to be set through the ref. */
  useEffect(() => {
    const box = allBox.current;
    if (!box) return;
    box.checked = areas.length > 0 && checkedAreas.size === areas.length;
    box.indeterminate = checkedAreas.size > 0 && checkedAreas.size < areas.length;
  }, [areas, checkedAreas]);

  const openModule = useMemo(
    () => modules.find((m) => m.id === openId) ?? null,
    [modules, openId],
  );
  const schema = openModule?.config_schema ?? [];

  /**
   * The run's own settings — depth, priority, evidence bar and so on.
   *
   * They live ON the base module in the database, because they are a prompt fragment like
   * everything else. But they are HOISTED OUT of the module editor here, because they set
   * the posture of the WHOLE run: applying a priority to one module and not the others
   * would produce an analysis arguing with itself.
   *
   * They were buried before. The knobs existed but were only visible if you happened to
   * open one specific module out of twenty-five, so in practice they had vanished — which
   * is exactly how the owner reported it (2026-08-04).
   */
  const settingsModule = useMemo(
    () => modules.find((m) => (m.config_schema?.length ?? 0) > 0) ?? null,
    [modules],
  );
  const settingsSchema = settingsModule?.config_schema ?? [];

  /** One line naming what this run is set to. Compact enough to read at a glance. */
  const runLine = settingsSchema
    .map((knob) => `${knob.key}=${knobValue(knob, settingsModule?.config ?? null, settings)}`)
    .join(' · ');

  /**
   * The library, grouped by business area.
   *
   * Built by walking the list IN SERVER ORDER rather than by iterating a fixed list of
   * areas: `seq` is how an administrator sequences the analysis, and it already encodes
   * both which area a block belongs to and where that block sits. Re-deriving the order
   * here would mean two places had an opinion about it.
   *
   * Modules with no area — the base — group under a heading that says so, and lead.
   */
  const groups = useMemo(() => {
    const out: Array<{ area: string; ungrouped: boolean; items: SpnInstructionModule[] }> = [];
    for (const module of modules) {
      const area = module.business_area ?? 'Applies to every area';
      const last = out[out.length - 1];
      if (last && last.area === area) last.items.push(module);
      else out.push({ area, ungrouped: !module.business_area, items: [module] });
    }
    return out;
  }, [modules]);

  const toggleArea = (area: string) =>
    setCheckedAreas((prev) => {
      const next = new Set(prev);
      if (!next.delete(area)) next.add(area);
      return next;
    });

  const toggleAll = (on: boolean) => setCheckedAreas(on ? new Set(areas) : new Set());

  const addCustomArea = () => {
    const value = areaDraft.trim();
    if (!value) return;
    const clash =
      areas.some((a) => a.toLowerCase() === value.toLowerCase()) ||
      customAreas.some((a) => a.toLowerCase() === value.toLowerCase());
    if (clash) {
      say('That area is already on the list.');
      setAreaDraft('');
      return;
    }
    setCustomAreas((prev) => [...prev, value]);
    setAreaDraft('');
  };

  const addUnit = () => {
    const value = unitDraft.trim();
    if (!value) return;
    if (businessUnits.some((u) => u.toLowerCase() === value.toLowerCase())) {
      say('That business unit is already on the list.');
      setUnitDraft('');
      return;
    }
    setBusinessUnits((prev) => [...prev, value]);
    setUnitDraft('');
  };

  const toggleRunModule = (id: string) =>
    setRunModules((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  /** Replace one module in the library with what the server just returned. */
  const absorb = (module: SpnInstructionModule) => {
    setModules((prev) => prev.map((m) => (m.id === module.id ? module : m)));
    setNameDraft(module.name ?? '');
    setDescDraft(module.description ?? '');
    setBodyDraft(module.body ?? '');
    setConfigDraft({ ...(module.config ?? {}) });
  };

  const save = async () => {
    if (!openModule) return;
    setBusy(true);
    try {
      const { module } = await api.spn.updateInstruction(openModule.id, {
        name: nameDraft.trim() || openModule.name,
        description: descDraft,
        body: bodyDraft,
        config: schema.length ? configDraft : {},
      });
      absorb(module);
      say('Saved.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    if (!openModule) return;
    setBusy(true);
    try {
      const { module } = await api.spn.resetInstruction(openModule.id);
      absorb(module);
      say('Reverted to the shipped default.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    if (!openModule) return;
    setBusy(true);
    try {
      const { module } = await api.spn.createInstruction({
        name: `${nameDraft.trim() || openModule.name} (copy)`,
        description: descDraft || openModule.description || '',
        // The copy keeps its original's area and sits directly after it, so it appears in
        // the group the user was working in rather than at the bottom of the library.
        business_area: openModule.business_area,
        after_seq: openModule.seq,
        body: bodyDraft,
        ...(schema.length ? { config_schema: schema, config: configDraft } : {}),
      });
      setModules((prev) =>
        [...prev, module].sort((a, b) => a.seq - b.seq || a.name.localeCompare(b.name)),
      );
      if (module.business_area) {
        setOpenGroups((prev) => new Set(prev).add(module.business_area!));
      }
      open(module);
      say('Duplicated — edit the copy freely; the original is untouched.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Remove a module from the library.
   *
   * NOT in the approved prototype, which showed New and Duplicate and no way back — a
   * library that can only grow. A system module is hidden rather than deleted server-side,
   * so the base analysis cannot be lost; that is why the wording differs by kind.
   */
  const remove = async (target: SpnInstructionModule) => {
    const ok = window.confirm(
      target.is_system
        ? `Remove "${target.name}" from the library? It shipped with the product, so it is ` +
          'hidden rather than destroyed and can be brought back.'
        : `Delete "${target.name}"? This cannot be undone.`,
    );
    if (!ok) return;

    setBusy(true);
    try {
      await api.spn.deleteInstruction(target.id);
      setModules((prev) => prev.filter((m) => m.id !== target.id));
      setRunModules((prev) => {
        const next = new Set(prev);
        next.delete(target.id);
        return next;
      });
      // Only clear the editor if the module being removed is the one open in it.
      if (openId === target.id) setOpenId(null);
      say(target.is_system ? 'Removed from the library.' : 'Deleted.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addModule = async () => {
    setBusy(true);
    try {
      // Filed into whichever group is open in the editor, so "+ New" from inside Human
      // Resources creates a Human Resources module rather than an orphan.
      const { module } = await api.spn.createInstruction({
        name: 'New instruction module',
        description: 'Added for this client.',
        ...(openModule?.business_area
          ? { business_area: openModule.business_area, after_seq: openModule.seq }
          : {}),
        body: '',
      });
      setModules((prev) =>
        [...prev, module].sort((a, b) => a.seq - b.seq || a.name.localeCompare(b.name)),
      );
      if (module.business_area) {
        setOpenGroups((prev) => new Set(prev).add(module.business_area!));
      }
      open(module);
      say('Module created. Give it a name and a body, then save.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Compile through the SERVER, not in the browser.
   *
   * The knob directives that turn a chosen value into a sentence live server-side and are
   * what the run itself will use. Compiling a second time here would let the text the user
   * reads drift from the text that is sent, which is the one thing this preview exists to
   * prevent. Unsaved knob edits ride along as overrides so the preview matches the screen.
   */
  const compile = async () => {
    setBusy(true);
    try {
      const { prompt } = await api.spn.compileDiscoveryPrompt({
        moduleIds: [...runModules],
        areas: [...checkedAreas],
        customAreas,
        // The run settings, not the open module's stored config — what the user has set
        // above the library is what the run must actually use.
        ...(settingsModule ? { overrides: { [settingsModule.id]: settings } } : {}),
      });
      setCompiled(prompt);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copyPrompt = async () => {
    setBusy(true);
    try {
      const { prompt } = await api.spn.compileDiscoveryPrompt({
        moduleIds: [...runModules],
        areas: [...checkedAreas],
        customAreas,
        // The run settings, not the open module's stored config — what the user has set
        // above the library is what the run must actually use.
        ...(settingsModule ? { overrides: { [settingsModule.id]: settings } } : {}),
      });
      await navigator.clipboard.writeText(prompt);
      say('Copied — paste it into Claude, or send it to a colleague.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Start the run and go straight to the session.
   *
   * The request returns a session code in milliseconds; generation continues on the
   * server. Navigating rather than waiting means closing this tab cannot lose the run.
   */
  const start = async () => {
    setStarting(true);
    setError(undefined);
    try {
      const { sessionCode } = await api.spn.startDiscovery({
        moduleIds: [...runModules],
        areas: [...checkedAreas],
        customAreas,
        businessUnits,
        // The run settings, not the open module's stored config — what the user has set
        // above the library is what the run must actually use.
        ...(settingsModule ? { overrides: { [settingsModule.id]: settings } } : {}),
      });
      navigate(`/spn/discoveries/${sessionCode}`);
    } catch (e) {
      setError((e as Error).message);
      setStarting(false);
    }
  };

  if (loading) return <p className="nd-note">Loading…</p>;

  const areaTotal = checkedAreas.size + customAreas.length;
  // Both are required by the server too — this only spares the user a round trip to be
  // told what the screen can already see.
  const blocked =
    areaTotal === 0
      ? 'Tick at least one business area before running.'
      : runModules.size === 0
        ? 'Tick at least one instruction module before running.'
        : null;

  return (
    <div className="nd">
      <PageHeader title="New Discovery" description="Find where the business is losing margin — and what to do about it." />

      {/* No "Back to Discoveries" button — the breadcrumb handles it (owner's ruling,
          2026-08-04). */}
      <HelpPanel help={NEW_HELP} />

      {error ? <p className="nd-err">{error}</p> : null}

      {/* ── Company ─────────────────────────────────────────────────────── */}
      <div className="nd-card">
        {/* No sub-heading. It explained WHY the company is not a selector, which is a
            design note for whoever built the screen, not information the person running a
            discovery needs. The "Edit the company profile" link below already says where
            the value comes from, and says it at the point where someone would act on it. */}
        <h2>Company</h2>
        <div style={{ fontSize: 20, fontWeight: 600, marginTop: 10 }}>
          {company?.companyName || 'No company recorded'}
        </div>
        <div style={{ marginTop: 8 }}>
          <span className="nd-note" style={{ marginRight: 8 }}>
            SIC codes
          </span>
          {company?.sicCodes?.length ? (
            company.sicCodes.map((code, i) => (
              <span key={code} className="nd-chip static" title={i === 0 ? 'Primary classification' : undefined}>
                {code}
                {i === 0 ? ' · primary' : ''}
              </span>
            ))
          ) : (
            <span className="nd-note">
              none recorded — the analysis will infer the industry and say that it did
            </span>
          )}
        </div>
        <p className="nd-note" style={{ marginTop: 10, marginBottom: 0 }}>
          Wrong company or missing codes?{' '}
          <Link to="/admin/company-profile">Edit the company profile</Link>.
        </p>
      </div>

      {/* ── Business areas ──────────────────────────────────────────────── */}
      <div className="nd-card">
        <h2>Business areas to analyse</h2>
        <p className="nd-hint">
          Only the areas you tick are analysed. Add your own if the {areas.length} below do
          not cover it.
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            paddingBottom: 8,
            marginBottom: 6,
            borderBottom: '1px solid #eef1f6',
          }}
        >
          <label className="nd-row" style={{ padding: 0, fontWeight: 600 }}>
            <input ref={allBox} type="checkbox" onChange={(e) => toggleAll(e.target.checked)} />
            Select all
          </label>
          <span className="nd-note">
            {checkedAreas.size} of {areas.length} selected
          </span>
        </div>

        <div className="nd-grid2">
          {areas.map((area) => (
            <label className="nd-row" key={area}>
              <input
                type="checkbox"
                checked={checkedAreas.has(area)}
                onChange={() => toggleArea(area)}
              />
              {area}
            </label>
          ))}
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'center', maxWidth: 520 }}>
          <input
            type="text"
            value={areaDraft}
            onChange={(e) => setAreaDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter adds the area rather than submitting anything — there is no form to
              // submit, and losing what you typed to a stray keystroke is worse than the
              // shortcut is worth.
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomArea();
              }
            }}
            placeholder="Add a business area — e.g. Regulatory Affairs, Field Service"
          />
          <button className="nd-btn ghost sm" onClick={addCustomArea}>
            Add
          </button>
        </div>

        {customAreas.length ? (
          <div>
            {customAreas.map((area) => (
              <span className="nd-chip" key={area}>
                {area}
                <button
                  type="button"
                  aria-label={`Remove ${area}`}
                  onClick={() => setCustomAreas((prev) => prev.filter((a) => a !== area))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {/* ── Business units ──────────────────────────────────────────────── */}
      <div className="nd-card">
        <h2>Business units</h2>
        <p className="nd-hint">
          A client does not experience itself as eight generic areas — it has divisions,
          sites and entities with their own names. Add yours and every finding is attributed
          to one of them. Leave this empty and the analysis treats the company as a whole.
        </p>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 520 }}>
          <input
            type="text"
            value={unitDraft}
            onChange={(e) => setUnitDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addUnit();
              }
            }}
            placeholder="Add a business unit — e.g. Research Models & Services, Safety Assessment"
          />
          <button className="nd-btn ghost sm" onClick={addUnit}>
            Add
          </button>
        </div>

        {businessUnits.length ? (
          <div>
            {businessUnits.map((unit) => (
              <span className="nd-chip" key={unit}>
                {unit}
                <button
                  type="button"
                  aria-label={`Remove ${unit}`}
                  onClick={() => setBusinessUnits((prev) => prev.filter((u) => u !== unit))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="nd-note" style={{ marginTop: 10, marginBottom: 0 }}>
            All business units — the company is analysed as one.
          </p>
        )}
      </div>

      {/* ── Analysis instructions ───────────────────────────────────────── */}
      <div className="nd-card" style={{ paddingBottom: 14 }}>
        <h2>Analysis instructions</h2>
        <p className="nd-hint">
          A library of instruction modules, in the same shape as APR&apos;s negotiation
          instructions: each one is editable, each keeps its shipped default so your edits
          can be reverted, and the knobs at the top compile into the text that is sent.
          Tick the modules this run should use.
        </p>

        {/* ── Run settings ─────────────────────────────────────────────────
            Above the library, because they apply to every module in the run rather than to
            whichever one is open. Each value injects a directive into the compiled prompt;
            they are not labels. */}
        {settingsSchema.length ? (
          <div className="nd-settings">
            <div className="head">Run settings — these shape the whole analysis</div>
            <div className="nd-knobs" style={{ margin: 0 }}>
              {settingsSchema.map((knob) => (
                <div className="nd-knob" key={knob.key}>
                  <label htmlFor={`run-${knob.key}`}>{knob.label}</label>
                  <select
                    id={`run-${knob.key}`}
                    value={knobValue(knob, settingsModule?.config ?? null, settings)}
                    onChange={(e) =>
                      setSettings((prev) => ({ ...prev, [knob.key]: e.target.value }))
                    }
                  >
                    {knob.values.map((value) => (
                      <option key={value} value={value}>
                        {/* The label carries what the setting actually does — "deep — up to
                            20 per area". A bare "deep" told a reader nothing about what the
                            run would cost or return. */}
                        {knob.labels?.[value] ?? value}
                      </option>
                    ))}
                  </select>
                  {knob.hint ? <div className="kh">{knob.hint}</div> : null}
                </div>
              ))}
            </div>
            <div className="nd-runline" style={{ marginTop: 12 }}>
              {runLine}
            </div>
          </div>
        ) : null}

        {/* What is actually in the run, listed by area.
            Modules can be picked from any number of areas — two from Order Management, one
            from Supply Chain, one from Financial Management — and with the groups collapsed
            there would otherwise be nothing on screen saying so. */}
        {runModules.size > 0 ? (
          <div style={{ marginBottom: 12 }}>
            {groups
              .filter((g) => g.items.some((m) => runModules.has(m.id)))
              .map((g) => (
                <span className="nd-chip" key={g.area}>
                  {g.area}
                  <b style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {g.items.filter((m) => runModules.has(m.id)).length}
                  </b>
                  <button
                    type="button"
                    aria-label={`Remove every ${g.area} module from the run`}
                    title={`Remove every ${g.area} module from the run`}
                    onClick={() =>
                      setRunModules((prev) => {
                        const next = new Set(prev);
                        g.items.forEach((m) => next.delete(m.id));
                        return next;
                      })
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
          </div>
        ) : null}

        <div className="nd-panel">
          <div className="nd-lib">
            <div className="nd-libhead">
              <span>Modules</span>
              {isAdmin ? (
                <button
                  className="nd-btn ghost sm"
                  style={{ padding: '2px 8px' }}
                  onClick={addModule}
                  disabled={busy}
                >
                  + New
                </button>
              ) : null}
            </div>

            {groups.map((group) => {
              const expanded = openGroups.has(group.area);
              const picked = group.items.filter((m) => runModules.has(m.id)).length;
              return (
                <div key={group.area}>
                  <button
                    type="button"
                    className="nd-group"
                    onClick={() => toggleGroup(group.area)}
                    aria-expanded={expanded}
                  >
                    <span className="caret">{expanded ? '▾' : '▸'}</span>
                    {group.area}
                    {/* The count is what a collapsed group can still tell you: how many of
                        its modules are in the run. Without it, closing a group hides the
                        only evidence that you chose anything in it. */}
                    <span className={`count${picked ? ' on' : ''}`}>
                      {picked}/{group.items.length}
                    </span>
                  </button>

                  {expanded
                    ? group.items.map((module) => (
                        <div
                          key={module.id}
                          className={`nd-libitem${module.id === openId ? ' on' : ''}`}
                          onClick={() => open(module)}
                        >
                          <div>
                            <input
                              type="checkbox"
                              style={{ marginRight: 6 }}
                              checked={runModules.has(module.id)}
                              // The tick chooses what RUNS; the row chooses what is being
                              // EDITED. Without this the row handler fires too and reverses
                              // the tick.
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleRunModule(module.id)}
                            />
                            <span className="nm">{module.name}</span>
                            {/* Delete lives ON THE ROW as well as in the editor toolbar.
                                The toolbar copy is only reachable once a module is open,
                                which is why the owner reported there was no way to delete
                                a prompt at all. */}
                            {isAdmin ? (
                              <button
                                type="button"
                                className="row-del"
                                style={{ float: 'right' }}
                                title={`Delete "${module.name}"`}
                                aria-label={`Delete ${module.name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void remove(module);
                                }}
                              >
                                ✕
                              </button>
                            ) : null}
                          </div>
                          <div className="ds">
                            <span className={`nd-tag ${module.is_system ? 'sys' : 'own'}`}>
                              {module.is_system ? 'System' : 'Yours'}
                            </span>{' '}
                            {module.description}
                          </div>
                        </div>
                      ))
                    : null}
                </div>
              );
            })}
          </div>

          <div className="nd-body">
            {openModule ? (
              <>
                {/* Name and description are EDITABLE, not a heading. The owner asked to be
                    able to edit any module, and a module whose text can be rewritten but
                    whose name cannot ends up describing something it no longer does. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    aria-label="Module name"
                    style={{ flex: 1, fontSize: 15, fontWeight: 600 }}
                  />
                  <span className={`nd-tag ${openModule.is_system ? 'sys' : 'own'}`}>
                    {openModule.is_system ? 'System' : 'Yours'}
                  </span>
                  {openModule.business_area ? (
                    <span className="nd-note">{openModule.business_area}</span>
                  ) : null}
                </div>
                <input
                  type="text"
                  value={descDraft}
                  onChange={(e) => setDescDraft(e.target.value)}
                  aria-label="Module description"
                  placeholder="One line on what this module makes the analysis look for"
                  style={{ width: '100%', marginTop: 8, fontSize: 12.5 }}
                />

                {/* The knobs are NOT rendered here. They live on this module in the
                    database but govern the whole run, so they are shown once at the top of
                    the card as Run settings — see `settingsModule`. Rendering them in both
                    places would give a user two controls for one value. */}
                {schema.length ? (
                  <p className="nd-note" style={{ marginTop: 10 }}>
                    This module carries the run settings shown above the library.
                  </p>
                ) : null}

                <textarea
                  className="nd-editor"
                  value={bodyDraft}
                  onChange={(e) => setBodyDraft(e.target.value)}
                  placeholder="What should this module make the analysis look for?"
                />

                <div className="nd-bar">
                  <button className="nd-btn" onClick={save} disabled={busy}>
                    Save
                  </button>
                  <button
                    className="nd-btn grey sm"
                    onClick={revert}
                    disabled={busy || openModule.default_body == null}
                    title={
                      openModule.default_body == null
                        ? 'This module was created here, so there is no shipped default to return to.'
                        : 'Discard every edit and return to the text that shipped.'
                    }
                  >
                    Revert to default
                  </button>
                  <button className="nd-btn grey sm" onClick={duplicate} disabled={busy || !isAdmin}>
                    Duplicate
                  </button>
                  <button className="nd-btn grey sm" onClick={copyPrompt} disabled={busy}>
                    Copy prompt
                  </button>
                  {isAdmin ? (
                    <button
                      className="nd-btn grey sm nd-danger"
                      onClick={() => remove(openModule)}
                      disabled={busy}
                      title={
                        openModule.is_system
                          ? 'Shipped modules are hidden rather than destroyed, so this is recoverable.'
                          : 'Delete this module from the library.'
                      }
                    >
                      Delete
                    </button>
                  ) : null}
                  <span className="nd-spacer" />
                  {flash ? <span className="nd-ok">{flash}</span> : null}
                  <span className="nd-note">
                    {runModules.size} of {modules.length} modules selected
                  </span>
                </div>
              </>
            ) : (
              <div style={{ padding: '28px 4px' }}>
                <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 6px' }}>
                  Pick the modules this run should use.
                </p>
                <p className="nd-note" style={{ margin: 0, maxWidth: 560 }}>
                  Open a business area on the left to see its instruction modules. Tick the
                  ones this discovery should run; click a module&apos;s name to read or edit
                  what it tells Claude to look for. Nothing is selected to begin with —
                  every module you tick costs tokens and lengthens the run.
                </p>
                <p className="nd-note" style={{ marginTop: 12 }}>
                  {modules.length} modules across {groups.length} groups.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="nd-bar" style={{ marginTop: 16 }}>
          <button
            className="nd-btn"
            style={{ padding: '10px 20px' }}
            onClick={start}
            disabled={starting || Boolean(blocked)}
            title={blocked ?? 'Send this to Claude and open the session.'}
          >
            {starting ? 'Starting…' : 'Start analysis'}
          </button>
          <button className="nd-btn ghost sm" onClick={compile} disabled={busy}>
            {compiled ? 'Refresh compiled prompt' : 'Preview compiled prompt'}
          </button>
          {/* "Hide preview", not "Hide" — the module toolbar above has its own Hide, for
              system modules, and two bare Hides on one screen mean two different things. */}
          {compiled ? (
            <button className="nd-btn grey sm" onClick={() => setCompiled(null)}>
              Hide preview
            </button>
          ) : null}
          <span className="nd-note">
            {checkedAreas.size} business area{checkedAreas.size === 1 ? '' : 's'}
            {customAreas.length ? ` + ${customAreas.length} custom` : ''} ·{' '}
            {runModules.size} instruction module{runModules.size === 1 ? '' : 's'} · output
            budget <b>{tokenBudget.toLocaleString('en-US')} tokens</b>
          </span>
        </div>

        {blocked ? (
          <p className="nd-note" style={{ marginTop: 8, marginBottom: 0 }}>
            {blocked}
          </p>
        ) : null}

        {compiled ? <pre className="nd-compiled">{compiled}</pre> : null}
      </div>
    </div>
  );
}
